import { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ACL_RELATIONSHIPS from "acl-relationships";
import { BaseForm, FormInput, FormSkeleton } from "components/BaseForm";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { usePermissionCatalog, useRoleMutations } from "hooks/useRolesTable";
import RoleService from "services/Role";
import RolePermissionsField from "routes/admin/components/RolePermissionsField";
import {
  buildCategoryPermissions,
  getAllowedCategories,
  serializePermissions,
} from "routes/admin/components/rolePermissions";
import { ROLES_PATH } from "routes/admin/components/roles_table/RolesTableConstants";

const endpointSchema = z.object({
  path: z.string(),
  method: z.string(),
  description: z.string().nullish(),
  is_allowed: z.boolean(),
});

const schema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  category_permissions: z
    .record(z.string(), z.array(endpointSchema))
    .refine(
      categoryPermissions =>
        getAllowedCategories(categoryPermissions).length > 0,
      "Select at least one permission"
    ),
});

const getDefaultName = (role, isClone) => {
  if (!role) return "";
  return isClone ? `Copy of ${role.name}` : role.name;
};

const RoleFormInner = ({ role, isEdit, isClone, catalog, pageTitle }) => {
  const navigate = useNavigate();
  const { create, update } = useRoleMutations();

  const defaultValues = useMemo(
    () => ({
      name: getDefaultName(role, isClone),
      category_permissions: buildCategoryPermissions(
        catalog.paths,
        catalog.categories,
        role?.permissions
      ),
    }),
    [role, isClone, catalog]
  );

  const handleSubmit = async values => {
    const payload = {
      name: values.name,
      permissions: serializePermissions(values.category_permissions),
    };
    if (isEdit) {
      await update.mutateAsync({ id: role.id, ...payload });
    } else {
      await create.mutateAsync(payload);
    }
  };

  const handleSuccess = () => {
    toast.success(isEdit ? "Role updated" : "Role created");
    navigate(ROLES_PATH);
  };

  return (
    <BaseForm
      enablePageTitle
      enablePageTitleActions
      pageTitle={pageTitle}
      pageSubtitle={isEdit ? role.name : undefined}
      backLink={{ to: ROLES_PATH, label: "Back to Roles" }}
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={() => navigate(ROLES_PATH)}
      submitButtonText={isEdit ? "Save Role" : "Create Role"}
    >
      <div className="tw:flex tw:flex-col tw:gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
              <FormInput name="name" label="Name" required />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Permissions</CardTitle>
            <CardDescription>
              Add a category, then allow its actions as a whole or per route.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RolePermissionsField />
          </CardContent>
        </Card>
      </div>
    </BaseForm>
  );
};

RoleFormInner.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    permissions: PropTypes.array,
  }),
  isEdit: PropTypes.bool.isRequired,
  isClone: PropTypes.bool.isRequired,
  catalog: PropTypes.shape({
    paths: PropTypes.object.isRequired,
    categories: PropTypes.object.isRequired,
  }).isRequired,
  pageTitle: PropTypes.string.isRequired,
};

const getPageTitle = (isEdit, isClone) => {
  if (isEdit) return "Edit Role";
  return isClone ? "Clone Role" : "New Role";
};

function RoleForm({ mode }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = mode === "edit";
  // Cloning is a create that starts from an existing role's name and
  // permissions, passed as `?from=<role id>`.
  const cloneFromId = isEdit ? null : searchParams.get("from");
  const isClone = cloneFromId != null;
  const sourceRoleId = isEdit ? id : cloneFromId;

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.adminRole.edit
      : ACL_RELATIONSHIPS.adminRole.create,
  ]);

  const pageTitle = getPageTitle(isEdit, isClone);
  usePageTitle(pageTitle);

  const {
    data: role,
    isPending: rolePending,
    isError: roleError,
  } = useQuery({
    queryKey: ["admin-role", sourceRoleId],
    queryFn: () => RoleService.getRoleAsAdmin(sourceRoleId),
    enabled: sourceRoleId != null,
    retry: false,
    // The form reads its defaults once, on mount. A cached entry from an
    // earlier visit would mount it with pre-edit data, so the entry is
    // dropped as soon as this page unmounts.
    gcTime: 0,
  });
  const catalog = usePermissionCatalog();

  if (!canWrite) {
    return <Navigate to={ROLES_PATH} replace />;
  }

  if (roleError || catalog.isError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this role. Please try again."}</p>
        <Link to={ROLES_PATH}>Back to Roles</Link>
      </div>
    );
  }

  const ready = (sourceRoleId == null || !rolePending) && catalog.isSuccess;

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-6xl tw:flex-col tw:gap-4">
        {ready ? (
          <RoleFormInner
            role={sourceRoleId == null ? null : role}
            isEdit={isEdit}
            isClone={isClone}
            catalog={catalog.data}
            pageTitle={pageTitle}
          />
        ) : (
          <FormSkeleton sections={2} />
        )}
      </div>
    </div>
  );
}

RoleForm.propTypes = {
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
};

export default RoleForm;
